// src/components/assinatura/AtivarAssinatura.js — o cartão "Ativar assinatura
// agora" (out/26).
//
// No período de teste, Agenda, Automação e Formulários ficam fechados
// (BloqueioTeste desfoca a página e põe este cartão por cima); o selo
// "Período de teste" da TopBar e a tela Assinatura abrem o mesmo cartão numa
// janela (JanelaAtivar). Decisão do dono: a tela não mostra quantos dias de
// teste faltam — a contagem afastava quem estava gostando.
//
// O botão encerra o teste no Stripe e cobra a mensalidade NA HORA
// (POST /billing/ativar-assinatura). Antes do clique a pessoa vê quanto e
// em qual cartão (GET /billing/ativar-assinatura). Cartão recusado não muda
// nada: o teste continua e a frase do servidor diz o que fazer. Só o
// supervisor ativa; o atendente vê o cartão sem o botão.
//
// Visual: vidro escuro da marca nos dois temas (como a barra de lote do
// CRM), Syne no título e Outfit no resto. O momento marcante é o da página
// voltando ao foco depois de ativar — fica no BloqueioTeste.
import { useEffect, useId, useRef, useState } from 'react';
import { Box, ButtonBase, CircularProgress } from '@mui/material';
import {
  FiCalendar, FiZap, FiClipboard, FiLock, FiCheck, FiCreditCard, FiAlertTriangle, FiX,
} from 'react-icons/fi';
import api from '../../services/Api';
import { atualizarAssinatura } from '../../hooks/useAssinatura';

const SYNE = "'Syne', system-ui, sans-serif";
const OUTFIT = "'Outfit', system-ui, sans-serif";

export const COR = {
  vidro: 'rgba(11,18,32,0.94)',
  linha: 'rgba(255,255,255,0.08)',
  linha2: 'rgba(255,255,255,0.14)',
  painel: 'rgba(255,255,255,0.035)',
  texto: '#E9EEF6',
  texto2: '#93A1B5',
  texto3: '#5F6E83',
  verde: '#00D67C',
  verdeHover: '#1FEA94',
  verdeFundo: 'rgba(0,214,124,0.10)',
  verdeBorda: 'rgba(0,214,124,0.30)',
  ambar: '#FFB547',
  ambarFundo: 'rgba(255,181,71,0.08)',
  ambarBorda: 'rgba(255,181,71,0.32)',
};

// O que cada página fechada libera — dito com o que a pessoa reconhece da
// tela, não com o nome técnico. Mudou a página, muda aqui.
export const PAGINAS = {
  agenda: {
    nome: 'Agenda',
    icone: FiCalendar,
    titulo: 'A Agenda abre com a assinatura ativa',
    texto: 'No período de teste a Agenda fica fechada. Ative a assinatura e ela abre na hora, com tudo o que você já tem no Cetto.',
    itens: [
      'Consultas, retornos e tarefas da equipe num calendário só',
      'Lembrete no WhatsApp antes da consulta, com confirmação pelo botão',
      'O Copiloto acha horário livre e remarca para você',
    ],
    abrir: 'Abrir a Agenda',
    pronta: 'Pronto, a Agenda está liberada',
  },
  automacao: {
    nome: 'Automação',
    icone: FiZap,
    titulo: 'As automações abrem com a assinatura ativa',
    texto: 'No período de teste as automações e a IA ficam fechadas. Ative a assinatura e elas abrem na hora.',
    itens: [
      'Resposta automática no primeiro contato, número por número',
      'Mensagens prontas com imagem ou documento, sem gastar template',
      'IA que sugere respostas no chat e pode atender sozinha',
    ],
    abrir: 'Abrir as automações',
    pronta: 'Pronto, as automações estão liberadas',
  },
  formularios: {
    nome: 'Formulários',
    icone: FiClipboard,
    titulo: 'Os formulários abrem com a assinatura ativa',
    texto: 'No período de teste os formulários ficam fechados. Ative a assinatura e eles abrem na hora.',
    itens: [
      'Formulários que o cliente responde sem sair do WhatsApp',
      'As respostas preenchem o CRM e entram na pasta certa',
      'Envio em massa com a taxa de resposta de cada disparo',
    ],
    abrir: 'Abrir os formulários',
    pronta: 'Pronto, os formulários estão liberados',
  },
};

const GERAL = {
  icone: FiLock,
  titulo: 'Ative sua assinatura',
  texto: 'No período de teste, Agenda, Automação e Formulários ficam fechados. Ative a assinatura e tudo abre na hora.',
  itens: null,
  abrir: 'Continuar',
  pronta: 'Pronto, tudo liberado',
};

const NOME_PLANO = { STARTER: 'Starter', PLUS: 'Plus', BUSINESS: 'Business', ENTERPRISE: 'Enterprise' };
const BANDEIRA = { visa: 'Visa', mastercard: 'Mastercard', amex: 'American Express', elo: 'Elo', hipercard: 'Hipercard', diners: 'Diners', discover: 'Discover', jcb: 'JCB' };

const reais = (v, moeda = 'BRL') => Number(v).toLocaleString('pt-BR', { style: 'currency', currency: moeda || 'BRL' });
const diaDoMes = (iso) => {
  try {
    return new Date(iso).toLocaleDateString('pt-BR', { day: 'numeric', month: 'long', timeZone: 'America/Sao_Paulo' });
  } catch { return null; }
};
const cartaoTexto = (c) => (c ? `${BANDEIRA[c.bandeira] || 'Cartão'} final ${c.final}` : null);

function erroDaApi(err, padrao) {
  const d = err?.response?.data?.detail;
  return typeof d === 'string' ? d : padrao;
}

// ── Peças ────────────────────────────────────────────────────────────────

function Selo({ Icone, liberado }) {
  return (
    <Box aria-hidden sx={{
      position: 'relative', width: 52, height: 52, flexShrink: 0, borderRadius: '15px',
      display: 'grid', placeItems: 'center',
      color: COR.verde, bgcolor: COR.verdeFundo, border: `1px solid ${COR.verdeBorda}`,
      boxShadow: liberado ? `0 0 0 6px rgba(0,214,124,0.08), 0 0 28px rgba(0,214,124,0.35)` : 'none',
      transition: 'box-shadow .5s ease',
    }}>
      <Icone size={22} />
      <Box sx={{
        position: 'absolute', right: -6, bottom: -6, width: 22, height: 22, borderRadius: '50%',
        display: 'grid', placeItems: 'center',
        bgcolor: liberado ? COR.verde : '#1A2438', color: liberado ? '#06130D' : COR.texto2,
        border: `2px solid #0B1220`, transition: 'background-color .3s ease, color .3s ease',
      }}>
        {liberado ? <FiCheck size={12} strokeWidth={3} /> : <FiLock size={11} strokeWidth={2.5} />}
      </Box>
    </Box>
  );
}

function BotaoVerde({ children, ...props }) {
  return (
    <ButtonBase {...props} sx={{
      width: '100%', minHeight: 48, px: 2, borderRadius: '11px', gap: 1,
      fontFamily: SYNE, fontSize: '0.92rem', fontWeight: 700, letterSpacing: '0.01em',
      bgcolor: COR.verde, color: '#06130D',
      transition: 'background-color .15s ease, transform .15s ease',
      '&:hover': { bgcolor: COR.verdeHover },
      '&:active': { transform: 'translateY(1px)' },
      '&.Mui-disabled': { bgcolor: 'rgba(0,214,124,0.45)', color: '#06130D' },
      '&.Mui-focusVisible': { outline: `2px solid ${COR.texto}`, outlineOffset: 3 },
    }}>
      {children}
    </ButtonBase>
  );
}

function BotaoTexto({ children, ...props }) {
  return (
    <ButtonBase {...props} sx={{
      px: 1.2, py: 0.8, borderRadius: '8px', fontFamily: OUTFIT, fontSize: '0.86rem', fontWeight: 500,
      color: COR.texto2, '&:hover': { color: COR.texto },
      '&.Mui-focusVisible': { outline: `2px solid ${COR.verde}`, outlineOffset: 2 },
    }}>
      {children}
    </ButtonBase>
  );
}

// ── Cartão ───────────────────────────────────────────────────────────────

/**
 * @param {object} props
 * @param {'agenda'|'automacao'|'formularios'} [props.pagina]  Sem página = a janela geral (TopBar, Assinatura).
 * @param {() => void} [props.onCobrada]   Na hora em que a cobrança passa (antes do "continuar").
 * @param {() => void} [props.onAtivada]   Depois de cobrar e o usuário clicar em continuar.
 * @param {() => void} [props.onVoltar]    "Voltar ao painel" (só no bloqueio de página).
 * @param {() => void} [props.onFechar]    X da janela (só na janela).
 * @param {string} [props.tituloId]       id do título, pro aria-labelledby da janela.
 */
export default function AtivarAssinatura({ pagina, onCobrada, onAtivada, onVoltar, onFechar, tituloId: tituloIdDeFora }) {
  const conf = PAGINAS[pagina] || GERAL;
  const tituloRef = useRef(null);
  // Único por cartão: a página fechada e a janela do selo podem estar na tela juntas.
  const idProprio = useId();
  const tituloId = tituloIdDeFora || idProprio;
  const [previa, setPrevia] = useState(null);       // resposta do GET
  const [erroPrevia, setErroPrevia] = useState(false);
  const [ativando, setAtivando] = useState(false);
  const [erro, setErro] = useState('');
  const [feito, setFeito] = useState(null);         // resposta do POST

  useEffect(() => {
    let vivo = true;
    api.get('/billing/ativar-assinatura')
      .then((r) => {
        if (!vivo) return;
        setPrevia(r.data);
        // Ativou em outra aba (ou o teste acabou): o selo e o bloqueio somem.
        if (r.data && r.data.em_teste === false) atualizarAssinatura();
      })
      .catch(() => { if (vivo) setErroPrevia(true); });
    return () => { vivo = false; };
  }, []);

  useEffect(() => { tituloRef.current?.focus(); }, [feito]);

  const ativar = async () => {
    setAtivando(true);
    setErro('');
    try {
      const r = await api.post('/billing/ativar-assinatura');
      setFeito(r.data || { ativa: true });
      // Selo da TopBar e cadeados do menu saem já; o cartão de "pronto"
      // fica até o clique (quem o mostra segura a tela com onCobrada).
      onCobrada?.();
      atualizarAssinatura();
    } catch (e) {
      setErro(erroDaApi(e, 'Não deu para ativar agora. Nada foi cobrado: tente de novo em instantes.'));
    } finally {
      setAtivando(false);
    }
  };

  const continuar = () => onAtivada?.();

  const trocarCartao = async () => {
    try {
      const r = await api.post('/billing/customer-portal');
      if (r.data?.url) window.open(r.data.url, '_blank', 'noopener');
    } catch {
      setErro('Não deu para abrir a troca de cartão agora. Vá em Administrativo → Assinatura.');
    }
  };

  const plano = NOME_PLANO[previa?.plano] || previa?.plano || '';
  const cartao = cartaoTexto(previa?.cartao);
  const podeAtivar = previa ? previa.pode_ativar : true;   // até a prévia chegar, o botão espera
  const carregando = !previa && !erroPrevia;

  return (
    <Box
      // Na janela (JanelaAtivar) o papel de diálogo é do Dialog do MUI.
      role={onFechar ? undefined : 'dialog'}
      aria-labelledby={onFechar ? undefined : tituloId}
      data-screen-label="Ativar assinatura"
      sx={{
        position: 'relative', boxSizing: 'border-box', width: '100%', maxWidth: 500,
        bgcolor: COR.vidro, color: COR.texto, borderRadius: '20px',
        border: `1px solid ${COR.linha2}`,
        boxShadow: '0 30px 80px rgba(3,7,15,0.55), inset 0 1px 0 rgba(255,255,255,0.05)',
        backdropFilter: 'blur(14px)',
        p: { xs: '22px 20px 18px', sm: '28px 28px 20px' },
        fontFamily: OUTFIT,
        animation: 'cptAtivarEntra .32s cubic-bezier(.2,.8,.2,1) both',
        '@keyframes cptAtivarEntra': {
          from: { opacity: 0, transform: 'translateY(10px) scale(.985)' },
          to: { opacity: 1, transform: 'none' },
        },
        '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
      }}
    >
      {onFechar && !feito && (
        <ButtonBase onClick={onFechar} aria-label="Fechar" sx={{
          position: 'absolute', top: 14, right: 14, width: 32, height: 32, borderRadius: '9px',
          color: COR.texto3, '&:hover': { color: COR.texto, bgcolor: 'rgba(255,255,255,0.05)' },
          '&.Mui-focusVisible': { outline: `2px solid ${COR.verde}` },
        }}>
          <FiX size={17} />
        </ButtonBase>
      )}

      <Selo Icone={conf.icone} liberado={!!feito} />

      <Box sx={{
        mt: 2.2, fontFamily: SYNE, fontSize: '0.66rem', fontWeight: 700, letterSpacing: '0.16em',
        textTransform: 'uppercase', color: feito ? COR.verde : COR.texto3,
      }}>
        {feito ? 'Assinatura ativa' : 'Período de teste'}
      </Box>
      <Box component="h2" id={tituloId} ref={tituloRef} tabIndex={-1} sx={{
        m: 0, mt: 0.8, fontFamily: SYNE, fontWeight: 800, fontSize: { xs: '1.3rem', sm: '1.45rem' },
        lineHeight: 1.15, letterSpacing: '-0.02em', color: '#fff', outline: 'none', pr: onFechar ? 4 : 0,
      }}>
        {feito ? conf.pronta : conf.titulo}
      </Box>

      {feito ? (
        <>
          <Box component="p" sx={{ m: 0, mt: 1.2, fontSize: '0.95rem', lineHeight: 1.6, color: COR.texto2 }}>
            {feito.valor_cobrado
              ? <>Cobramos <b style={{ color: COR.texto }}>{reais(feito.valor_cobrado, previa?.moeda)}</b>{cartao ? <> no {cartao}</> : null}.</>
              : <>Sua assinatura está ativa.</>}
            {feito.proxima_cobranca && diaDoMes(feito.proxima_cobranca)
              ? <> A próxima cobrança é em {diaDoMes(feito.proxima_cobranca)}.</>
              : null}
          </Box>
          <Box sx={{ mt: 3 }}>
            <BotaoVerde onClick={continuar}>{onFechar ? 'Fechar' : conf.abrir}</BotaoVerde>
          </Box>
        </>
      ) : (
        <>
          <Box component="p" sx={{ m: 0, mt: 1.2, fontSize: '0.95rem', lineHeight: 1.6, color: COR.texto2 }}>
            {conf.texto}
          </Box>

          {conf.itens && (
            <Box component="ul" sx={{ listStyle: 'none', m: 0, mt: 2, p: 0, display: 'grid', gap: 1.1 }}>
              {conf.itens.map((t) => (
                <Box component="li" key={t} sx={{ display: 'flex', gap: 1.2, alignItems: 'flex-start', fontSize: '0.9rem', lineHeight: 1.45, color: COR.texto }}>
                  <Box aria-hidden sx={{ mt: '3px', flexShrink: 0, width: 16, height: 16, borderRadius: '50%', display: 'grid', placeItems: 'center', bgcolor: COR.verdeFundo, color: COR.verde }}>
                    <FiCheck size={10} strokeWidth={3} />
                  </Box>
                  {t}
                </Box>
              ))}
            </Box>
          )}

          {podeAtivar ? (
            <>
              {/* Quanto e onde, antes do clique — o botão cobra na hora. */}
              <Box sx={{ mt: 2.6, p: '14px 16px', borderRadius: '13px', bgcolor: COR.painel, border: `1px solid ${COR.linha}` }}>
                <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 2 }}>
                  <Box sx={{ fontWeight: 600, fontSize: '0.92rem' }}>
                    {plano ? `Plano ${plano}` : 'Seu plano'}
                  </Box>
                  <Box sx={{ fontWeight: 700, fontSize: '1.08rem', fontVariantNumeric: 'tabular-nums', color: '#fff', whiteSpace: 'nowrap' }}>
                    {carregando ? <Box component="span" sx={{ color: COR.texto3, fontWeight: 500, fontSize: '0.86rem' }}>calculando…</Box>
                      : previa?.valor != null ? reais(previa.valor, previa.moeda)
                        : <Box component="span" sx={{ color: COR.texto2, fontWeight: 500, fontSize: '0.86rem' }}>mensalidade do plano</Box>}
                  </Box>
                </Box>
                <Box sx={{ mt: 0.6, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, fontSize: '0.8rem', color: COR.texto2 }}>
                  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.8 }}>
                    <FiCreditCard size={13} aria-hidden />
                    {cartao || 'no cartão cadastrado'}
                  </Box>
                  <span>cobrado agora</span>
                </Box>
              </Box>

              {erro && (
                <Box role="alert" sx={{
                  mt: 1.6, p: '11px 13px', borderRadius: '11px', display: 'flex', gap: 1.1,
                  bgcolor: COR.ambarFundo, border: `1px solid ${COR.ambarBorda}`, color: COR.texto, fontSize: '0.85rem', lineHeight: 1.5,
                }}>
                  <Box sx={{ color: COR.ambar, mt: '2px', flexShrink: 0 }}><FiAlertTriangle size={15} aria-hidden /></Box>
                  <Box>
                    {erro}{' '}
                    <ButtonBase onClick={trocarCartao} sx={{
                      fontFamily: OUTFIT, fontSize: 'inherit', fontWeight: 600, color: COR.ambar, textDecoration: 'underline',
                      textUnderlineOffset: '3px', verticalAlign: 'baseline',
                      '&.Mui-focusVisible': { outline: `2px solid ${COR.ambar}`, outlineOffset: 2 },
                    }}>
                      Gerenciar cartão
                    </ButtonBase>
                  </Box>
                </Box>
              )}

              <Box sx={{ mt: 2 }}>
                <BotaoVerde onClick={ativar} disabled={ativando || carregando}>
                  {ativando ? <><CircularProgress size={16} thickness={5} sx={{ color: '#06130D' }} /> Ativando…</> : 'Ativar assinatura agora'}
                </BotaoVerde>
              </Box>
              <Box component="p" sx={{ m: 0, mt: 1.3, fontSize: '0.76rem', lineHeight: 1.55, color: COR.texto3, textAlign: 'center' }}>
                O teste termina agora e a mensalidade é cobrada neste cartão, depois todo mês na mesma data.
                Cancele quando quiser em Assinatura.
              </Box>
            </>
          ) : (
            <Box sx={{
              mt: 2.6, p: '13px 15px', borderRadius: '13px', bgcolor: COR.painel, border: `1px solid ${COR.linha}`,
              fontSize: '0.88rem', lineHeight: 1.55, color: COR.texto2,
            }}>
              Só o responsável pela conta pode ativar a assinatura. Peça para ele ativar em
              {' '}<b style={{ color: COR.texto }}>Administrativo → Assinatura</b>.
            </Box>
          )}

          {onVoltar && (
            <Box sx={{ mt: 1.2, display: 'flex', justifyContent: 'center' }}>
              <BotaoTexto onClick={onVoltar}>Voltar ao painel</BotaoTexto>
            </Box>
          )}
        </>
      )}
    </Box>
  );
}
